import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
  ArrowLeft, Send, Smile, Paperclip, Mic, Image as ImageIcon, FileText, X, Plus, Film, Camera, Undo2,
  Phone, Video, Volume2, VolumeX, Check, CheckCheck, Trash2, StopCircle, Users, Images, Clock, Loader2,
  MoreVertical, BadgeCheck, DownloadCloud, Ban, Copy, Forward, Reply, ChevronDown, BarChart3, Pin, Share2, X as XIcon,
  PhoneOutgoing, PhoneIncoming, PhoneMissed, VideoOff,
} from "lucide-react";
import ScheduleDialog from "@/components/chat/ScheduleDialog";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl, msgType } from "@/lib/uploads";
import { isLottieSticker, stickerPreview } from "@/lib/lottieStickers";
import LottieSticker from "@/components/chat/LottieSticker";
import ProductRefCard from "@/components/shared/ProductRefCard";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import CallOverlay from "@/components/chat/CallOverlay";
import AlbumViewer from "@/components/chat/AlbumViewer";
import { AudioPlayer, VideoThumb } from "@/components/chat/MediaPlayers";
import MediaViewer from "@/components/chat/MediaViewer";
import GifPicker from "@/components/chat/GifPicker";
import ForwardDialog from "@/components/chat/ForwardDialog";
import LinkifiedText from "@/components/chat/LinkifiedText";
import { PollView, PollComposer } from "@/components/chat/Poll";
import { CachedImg, autoDownloadAllowed, isMediaDownloaded, markMediaDownloaded } from "@/lib/mediaCache";
import { compressImage, dataSaverOn, videoPoster } from "@/lib/imageCompress";
import { encodeImageMeta, BlurImage } from "@/lib/blur";
import { recordSent } from "@/lib/dataMeter";
import { ultraLiteOn } from "@/lib/prefs";
import { playSent } from "@/lib/sound";
import { haptic } from "@/lib/nativeBridge";
import { getInitials, avatarColor } from "@/lib/avatar";
import { toast } from "sonner";

const EMOJIS = ["😀","😂","🥰","😎","🤔","👍","🙏","🔥","🎉","❤️","😢","😅","🤩","👏","💯","🙌","😴","🤝","👑","✨"];
const STICKERS = ["👑","🎉","🔥","❤️","😂","🥳","🙏","💎","🌟","🚀","🍾","💃","🕺","🎊","😇","🤗"];

function fileToDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// Gates media rendering per the user's auto-download setting (Off / Wi-Fi / Always).
function MediaGate({ kind, name, mediaKey, mine = false, children }) {
  const { t } = useI18n();
  // The sender already has their own files on this device — never gate them.
  const [loaded, setLoaded] = useState(() => mine || autoDownloadAllowed(kind) || isMediaDownloaded(mediaKey));
  useEffect(() => { if (loaded && mediaKey) markMediaDownloaded(mediaKey); }, [loaded, mediaKey]);
  if (loaded) return children;
  return (
    <button onClick={() => { markMediaDownloaded(mediaKey); setLoaded(true); }} data-testid="media-gate"
      className="flex items-center gap-2 rounded-xl bg-[var(--rc-surface-2)] border border-[var(--rc-border)] px-4 py-3 text-sm text-[var(--rc-gold)] min-w-[160px]">
      <DownloadCloud size={18} />
      <span className="min-w-0 truncate text-left">{t("tap_to_download")}{name ? ` · ${name}` : ""}</span>
    </button>
  );
}

function CallBubble({ m, mine, onCall }) {
  const { t } = useI18n();
  const meta = m.call_meta || {};
  const isVideo = meta.media === "video";
  const status = meta.status || "no_answer";
  const missedIncoming = !mine && status !== "completed";
  const isRed = missedIncoming || (mine && status === "failed");

  const fmtDur = (s) => {
    s = Math.max(0, parseInt(s || 0, 10));
    if (s >= 60) return `${Math.round(s / 60)} ${t("call_min")}`;
    return `${s || 1} ${t("call_sec")}`;
  };

  let title, sub;
  if (status === "completed") {
    title = isVideo ? t("call_video") : t("call_voice");
    sub = fmtDur(meta.duration);
  } else if (missedIncoming) {
    title = isVideo ? t("call_missed_video") : t("call_missed_voice");
    sub = t("call_tap_back");
  } else if (status === "failed") {
    title = t("call_failed"); sub = t("call_try_again");
  } else {
    title = isVideo ? t("call_video") : t("call_voice"); sub = t("call_no_answer");
  }

  const Icon = isVideo ? (isRed ? VideoOff : Video) : missedIncoming ? PhoneMissed : mine ? PhoneOutgoing : PhoneIncoming;
  const clickable = status !== "completed";
  const t0 = dayjs(m.created_at).format("h:mm A");

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"} rc-pop`} data-testid={`message-${m.id}`}>
      <div className="max-w-[78%] sm:max-w-[65%]">
        {!mine && m.sender_name && <span className="text-xs text-[var(--rc-gold)] ml-1 mb-0.5 block">{m.sender_name}</span>}
        <div onClick={() => clickable && onCall && onCall(isVideo)} data-testid={`call-bubble-${m.id}`}
          className={`flex items-center gap-3 rounded-2xl pl-2.5 pr-4 py-2.5 ${clickable ? "cursor-pointer" : ""} ${mine ? "bg-[var(--rc-bubble-out)] rounded-br-md" : "bg-[var(--rc-bubble-in)] border border-[var(--rc-border)] rounded-bl-md"}`}>
          <span className={`h-11 w-11 shrink-0 grid place-items-center rounded-full ${isRed ? "bg-red-500/15" : "bg-black/20"}`}>
            <Icon size={20} className={isRed ? "text-red-400" : "text-[var(--rc-gold)]"} />
          </span>
          <div className="min-w-0">
            <div className="font-semibold text-[15px] leading-tight text-[var(--rc-text)]">{title}</div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-[var(--rc-muted)]">{sub}</span>
              <span className="text-[11px] text-[var(--rc-muted)]/70">{t0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ m, mine, peerId, receiptsOn, onReact, onDelete, onOpenMedia, onCopy, onForward, onReply, onPin, canPin, onCall }) {
  const { t } = useI18n();
  const [showActions, setShowActions] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [expandedText, setExpandedText] = useState(false);
  const pressTimer = useRef(null);
  const touchStart = useRef(null);
  const bubbleRef = useRef(null);
  const openedAt = useRef(0);
  const justSwiped = useRef(0);
  const reactions = Object.values(m.reactions || {});
  const counts = reactions.reduce((a, e) => ((a[e] = (a[e] || 0) + 1), a), {});
  const read = mine && peerId && receiptsOn && (m.read_by || []).includes(peerId);
  const hasText = (m.type === "text" || m.type === "sticker") && m.text;
  const mtype = msgType(m.type);

  const openMenu = () => {
    const r = bubbleRef.current?.getBoundingClientRect();
    setMenuPos(r ? { top: r.top, bottom: r.bottom, left: r.left, right: r.right } : null);
    openedAt.current = Date.now();
    setShowActions(true);
    if (navigator.vibrate) try { navigator.vibrate(8); } catch {}
  };
  const closeMenu = () => setShowActions(false);

  // Single tap opens the action menu; swipe-right still replies.
  const handleTap = () => {
    if (m.deleted) return;
    if (Date.now() - justSwiped.current < 400) return; // ignore the click after a swipe
    openMenu();
  };

  const startPress = (e) => {
    const tp = e.touches?.[0];
    touchStart.current = tp ? { x: tp.clientX, y: tp.clientY, dx: 0, dy: 0 } : null;
  };
  const movePress = (e) => {
    const tp = e.touches?.[0];
    if (tp && touchStart.current) {
      touchStart.current.dx = tp.clientX - touchStart.current.x;
      touchStart.current.dy = Math.abs(tp.clientY - touchStart.current.y);
    }
  };
  const endPress = () => {
    const s = touchStart.current;
    if (s && s.dx > 60 && s.dy < 45 && !m.deleted) { justSwiped.current = Date.now(); onReply(m); } // swipe right to reply
    touchStart.current = null;
  };

  const REACTIONS = ["❤️", "👍", "👎", "😂", "😮", "😢", "🙏"];
  const MENU_W = 240;
  let popStyle = {};
  if (menuPos) {
    const left = mine
      ? Math.max(8, menuPos.right - MENU_W)
      : Math.min(menuPos.left, window.innerWidth - MENU_W - 8);
    const top = Math.max(56, Math.min(menuPos.bottom + 10, window.innerHeight - 380));
    popStyle = { left, top, width: MENU_W };
  }

  const MenuRow = ({ icon: Icon, label, onClick, danger, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-4 px-5 py-3 text-left text-[15px] transition-colors hover:bg-[var(--rc-surface)] ${danger ? "text-red-400" : "text-[var(--rc-text)]"}`}>
      <Icon size={19} className={danger ? "text-red-400" : "text-[var(--rc-muted)]"} /> {label}
    </button>
  );

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"} rc-pop`} data-testid={`message-${m.id}`}>
      <div className="max-w-[78%] sm:max-w-[65%] relative">
        {!mine && m.sender_name && (
          <span className="text-xs text-[var(--rc-gold)] ml-1 mb-0.5 block">{m.sender_name}</span>
        )}
        <div ref={bubbleRef} onClick={handleTap} onTouchStart={startPress} onTouchMove={movePress} onTouchEnd={endPress} onTouchCancel={endPress} onContextMenu={(e) => { e.preventDefault(); openMenu(); }}
          style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
          className={`relative rounded-2xl px-3.5 py-2 cursor-pointer ${mine ? "bg-[var(--rc-bubble-out)] rounded-br-md" : "bg-[var(--rc-bubble-in)] border border-[var(--rc-border)] rounded-bl-md"}`}>
          {m.forwarded && !m.deleted && (
            <div className="flex items-center gap-1 mb-0.5 text-[11px] italic text-[var(--rc-muted)]" data-testid={`forwarded-tag-${m.id}`}>
              <Forward size={11} /> {t("forwarded_label")}
            </div>
          )}
          {m.reply_snippet && !m.deleted && (
            <div className="mb-1.5 rounded-lg border-l-2 border-[var(--rc-gold)] bg-black/25 px-2 py-1" data-testid={`reply-quote-${m.id}`}>
              <div className="text-[11px] text-[var(--rc-gold)] font-medium truncate">{m.reply_snippet.sender_name}</div>
              <div className="text-[11px] text-[var(--rc-muted)] truncate">{stickerPreview(m.reply_snippet.preview) || `[${msgType(m.reply_snippet.type)}]`}</div>
            </div>
          )}
          {m.product_ref && !m.deleted && (
            <div className="mb-1.5"><ProductRefCard data={m.product_ref} /></div>
          )}
          {m.deleted ? (
            <span className="italic text-[var(--rc-muted)] text-sm">🚫 This message was deleted</span>
          ) : m.type === "poll" ? (
            <PollView m={m} />
          ) : m.type === "sticker" ? (
            /\.(gif|webp|png)$/i.test(m.media || "") || (m.media || "").startsWith("http")
              ? <img src={m.media && m.media.startsWith("http") ? m.media : fileUrl(m.media)} alt="sticker" data-testid={`sticker-img-${m.id}`}
                  onClick={(e) => { e.stopPropagation(); onOpenMedia(m.media && m.media.startsWith("http") ? m.media : fileUrl(m.media)); }}
                  className="w-32 h-32 object-contain cursor-pointer" />
              : isLottieSticker(m.text)
                ? <LottieSticker text={m.text} testid={`lottie-sticker-${m.id}`} />
                : <span className="text-6xl leading-none">{m.text}</span>
          ) : m.type === "image" ? (
            <MediaGate kind="image" mediaKey={m.media} mine={mine}>
              <BlurImage src={fileUrl(m.media)} hash={m.blur_hash} testid={`image-msg-${m.id}`}
                onClick={(e) => { e?.stopPropagation?.(); onOpenMedia(fileUrl(m.media)); }}
                className="inline-block rounded-xl max-h-72 cursor-pointer"
                imgClassName="block rounded-xl max-h-72 object-cover" />
            </MediaGate>
          ) : m.type === "gif" ? (
            <MediaGate kind="gif" mediaKey={m.media} mine={mine}>
              <CachedImg src={m.media && m.media.startsWith("http") ? m.media : fileUrl(m.media)} alt="gif" onClick={(e) => { e?.stopPropagation?.(); onOpenMedia(m.media && m.media.startsWith("http") ? m.media : fileUrl(m.media)); }} data-testid={`gif-msg-${m.id}`}
                className="rounded-xl max-h-60 object-contain cursor-pointer" />
            </MediaGate>
          ) : m.type === "video" ? (
            <span onClick={(e) => e.stopPropagation()}>
              <MediaGate kind="video" name={m.media_name} mediaKey={m.media} mine={mine}>
                <VideoThumb src={fileUrl(m.media)} poster={m.poster ? fileUrl(m.poster) : null} hash={m.blur_hash} onOpen={() => onOpenMedia(fileUrl(m.media))} />
              </MediaGate>
            </span>
          ) : mtype === "audio" ? (
            <span onClick={(e) => e.stopPropagation()}>
              <MediaGate kind="audio" name={t("mt_audio")} mediaKey={m.media} mine={mine}>
                <AudioPlayer src={fileUrl(m.media)} mine={mine} />
              </MediaGate>
            </span>
          ) : mtype === "document" ? (
            <div className="min-w-[190px] max-w-[16rem]" data-testid={`document-msg-${m.id}`}>
              {/\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(m.media_name || "") ? (
                <MediaGate kind="document" name={m.media_name} mediaKey={m.media} mine={mine}>
                  <button onClick={(e) => { e.stopPropagation(); onOpenMedia(fileUrl(m.media)); }} className="block w-full mb-2 rounded-lg overflow-hidden border border-[var(--rc-border)]">
                    <img src={fileUrl(m.media)} alt="" data-testid={`doc-image-${m.id}`} className="w-full max-h-56 object-cover object-top" />
                  </button>
                </MediaGate>
              ) : /\.(pdf|docx?|odt|rtf|pptx?|odp|xlsx?|ods)$/i.test(m.media_name || "") ? (
                <MediaGate kind="document" name={m.media_name} mediaKey={m.media} mine={mine}>
                  <button onClick={(e) => { e.stopPropagation(); window.open(fileUrl(m.media), "_blank", "noopener"); }} className="block w-full mb-2 rounded-lg overflow-hidden border border-[var(--rc-border)]">
                    <img src={fileUrl(`${m.media}/thumb`)} alt="" data-testid={`pdf-thumb-${m.id}`}
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                      className="w-full max-h-56 object-cover object-top bg-white" />
                  </button>
                </MediaGate>
              ) : null}
              <div className="flex items-center gap-2.5">
                <span className="h-11 w-11 grid place-items-center rounded-lg bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] shrink-0"><FileText size={22} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate text-[var(--rc-text)]">{m.media_name || "Document"}</div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <button onClick={(e) => { e.stopPropagation(); window.open(fileUrl(m.media), "_blank", "noopener"); }} data-testid={`doc-open-${m.id}`} className="text-xs text-[var(--rc-gold)] font-medium hover:underline">{t("open")}</button>
                    <a href={fileUrl(m.media)} download={m.media_name} onClick={(e) => e.stopPropagation()} data-testid={`doc-download-${m.id}`} className="text-xs text-[var(--rc-muted)] hover:text-[var(--rc-text)]">{t("download")}</a>
                  </div>
                </div>
              </div>
            </div>
          ) : (m.type === "product" && !m.text) ? null : (() => {
            const full = m.text || "";
            const LIMIT = 300;
            const long = m.type === "text" && full.length > LIMIT;
            const shown = long && !expandedText ? full.slice(0, LIMIT).trimEnd() + "… " : full;
            return (
              <>
                <LinkifiedText text={shown} embedYouTube={expandedText || !long} preview={ultraLiteOn() ? null : m.link_preview} className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed text-[15px] text-[var(--rc-text)]" />
                {long && (
                  <button onClick={(e) => { e.stopPropagation(); setExpandedText((v) => !v); }} data-testid={`chat-readmore-${m.id}`}
                    className="block text-[var(--rc-gold)] text-xs font-medium mt-0.5 hover:underline">
                    {expandedText ? t("s_read_less") : t("s_read_more")}
                  </button>
                )}
              </>
            );
          })()}
          <div className={`flex items-center gap-1 mt-1 text-[10px] ${mine ? "justify-end text-[var(--rc-gold)]/70" : "text-[var(--rc-muted)]"}`}>
            {dayjs(m.created_at).format("HH:mm")}
            {mine && !m.deleted && (m.pending ? <Clock size={12} /> : read ? <CheckCheck size={13} className="text-[var(--rc-gold)]" /> : <Check size={13} />)}
          </div>
          {Object.keys(counts).length > 0 && (
            <div className="absolute -bottom-3 right-2 flex gap-0.5 bg-[var(--rc-surface-2)] border border-[var(--rc-border)] rounded-full px-1.5 py-0.5 text-xs">
              {Object.entries(counts).map(([e, n]) => <span key={e}>{e}{n > 1 ? n : ""}</span>)}
            </div>
          )}
        </div>
      </div>

      {showActions && !m.deleted && createPortal(
        <div className="fixed inset-0 z-[80]" data-testid={`msg-menu-${m.id}`}
          onClick={() => { if (Date.now() - openedAt.current > 300) closeMenu(); }}
          onTouchEnd={(e) => { if (Date.now() - openedAt.current < 300) { e.preventDefault(); e.stopPropagation(); } else closeMenu(); }}
          onContextMenu={(e) => { e.preventDefault(); closeMenu(); }}>
          <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px] rc-fade" />
          <div className="absolute rc-pop" style={popStyle} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-0.5 rounded-full bg-[var(--rc-surface-2)] border border-[var(--rc-border)] px-2 py-1.5 shadow-2xl">
              {REACTIONS.map((e) => (
                <button key={e} onClick={() => { onReact(m.id, e); closeMenu(); }} data-testid={`react-${m.id}-${e}`}
                  className="text-2xl leading-none hover:scale-125 active:scale-110 transition-transform">{e}</button>
              ))}
            </div>
            <div className="rounded-2xl bg-[var(--rc-surface-2)] border border-[var(--rc-border)] py-1.5 shadow-2xl overflow-hidden">
              <MenuRow icon={Reply} label={t("reply") || "Reply"} testid={`reply-msg-${m.id}`} onClick={() => { onReply(m); closeMenu(); }} />
              {hasText && <MenuRow icon={Copy} label={t("copy") || "Copy"} testid={`copy-msg-${m.id}`} onClick={() => { onCopy(m.text); closeMenu(); }} />}
              <MenuRow icon={Forward} label={t("forward") || "Forward"} testid={`forward-msg-${m.id}`} onClick={() => { onForward(m); closeMenu(); }} />
              {canPin && !m.deleted && <MenuRow icon={Pin} label={t("pin") || "Pin"} testid={`pin-msg-${m.id}`} onClick={() => { onPin(m.id); closeMenu(); }} />}
              {mine && <MenuRow icon={Trash2} label={t("delete") || "Delete"} danger testid={`delete-msg-${m.id}`} onClick={() => { onDelete(m.id); closeMenu(); }} />}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function AlbumBubble({ msgs, mine, peerId, receiptsOn, onOpenMedia, onForward }) {
  const last = msgs[msgs.length - 1];
  const read = mine && peerId && receiptsOn && (last.read_by || []).includes(peerId);
  const shown = msgs.slice(0, 4);
  const extra = msgs.length - shown.length;
  const gridCls = msgs.length === 2 ? "grid-cols-2" : "grid-cols-2";
  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"} rc-pop`} data-testid="album-bubble">
      <div className="max-w-[78%] sm:max-w-[60%] relative">
        {!mine && last.sender_name && <span className="text-xs text-[var(--rc-gold)] ml-1 mb-0.5 block">{last.sender_name}</span>}
        {last.forwarded && <span className="flex items-center gap-1 ml-1 mb-0.5 text-[11px] italic text-[var(--rc-muted)]" data-testid="forwarded-tag-album"><Forward size={11} /> Forwarded</span>}
        <button onClick={() => onForward(msgs)} data-testid="forward-album-btn" title="Forward"
          className={`absolute -top-9 ${mine ? "right-0" : "left-0"} z-10 h-7 flex items-center gap-1 rounded-full bg-[var(--rc-surface-2)] border border-[var(--rc-border)] px-2 text-[var(--rc-muted)] hover:text-[var(--rc-gold)] opacity-0 group-hover:opacity-100 transition-opacity`}>
          <Forward size={14} />
        </button>
        <div className={`rounded-2xl p-1.5 ${mine ? "bg-[var(--rc-bubble-out)] rounded-br-md" : "bg-[var(--rc-bubble-in)] border border-[var(--rc-border)] rounded-bl-md"}`}>
          <MediaGate kind="album" mediaKey={last?.id}>
          <div className={`grid ${gridCls} gap-1`}>
            {shown.map((m, idx) => (
              <button key={m.id} onClick={() => onOpenMedia(fileUrl(m.media))} data-testid={`album-item-${m.id}`}
                className="relative aspect-square overflow-hidden rounded-lg">
                <BlurImage src={fileUrl(m.media)} hash={m.blur_hash} className="w-full h-full" imgClassName="w-full h-full object-cover" />
                {idx === 3 && extra > 0 && (
                  <span className="absolute inset-0 grid place-items-center bg-black/55 text-white text-xl font-semibold">+{extra}</span>
                )}
              </button>
            ))}
          </div>
          </MediaGate>
          <div className={`flex items-center gap-1 mt-1 px-1 text-[10px] ${mine ? "justify-end text-[var(--rc-gold)]/70" : "text-[var(--rc-muted)]"}`}>
            {dayjs(last.created_at).format("HH:mm")}
            {mine && (read ? <CheckCheck size={13} className="text-[var(--rc-gold)]" /> : <Check size={13} />)}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function ChatThread({ chat, messages, setMessages, onBack, sendWs, sendMessage, typingName, onReload, onStartGroupCall, autoCall, onAutoCallConsumed }) {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  dayjs.locale(lang === "fr" ? "fr" : "en");
  const [text, setText] = useState("");
  const [picker, setPicker] = useState(null); // emoji | sticker
  const [attachOpen, setAttachOpen] = useState(false);
  const [gifPicker, setGifPicker] = useState(null); // null | 'gifs' | 'stickers'
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const composerRef = useRef(null);

  useEffect(() => {
    if (!attachOpen) return;
    const onDoc = (e) => { if (composerRef.current && !composerRef.current.contains(e.target)) setAttachOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [attachOpen]);
  const [readAloud, setReadAloud] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [dispMenu, setDispMenu] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const canPin = chat?.type === "direct" || chat?.is_admin;
  const pinMessage = async (id) => { try { await api.post(`/chats/${chat.id}/pin-message`, { message_id: id }); toast.success("Pinned"); } catch { toast.error("Only admins can pin"); } };
  const unpin = async () => { try { await api.post(`/chats/${chat.id}/unpin`); } catch {} };
  const shareChannel = async () => {
    try {
      let code = chat.invite_code;
      if (!code) { const { data } = await api.post(`/channels/${chat.id}/invite`); code = data.code; }
      const link = `${window.location.origin}/app?join=${code}`;
      if (navigator.share) { await navigator.share({ title: chat.title, text: `Join ${chat.title} on RoyalChat Nova`, url: link }); }
      else { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    } catch { toast.error("Could not create invite link"); }
  };
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [call, setCall] = useState(null); // {video}
  const [uploadPct, setUploadPct] = useState(null);
  const [albumOpen, setAlbumOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [hdrMenu, setHdrMenu] = useState(false);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const lastSpokenRef = useRef(null);
  const typingTimer = useRef(null);
  const lastIdRef = useRef(null);
  const adjustRef = useRef(null); // scrollHeight snapshot for prepend position preservation
  const [hasMore, setHasMore] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [atBottom, setAtBottom] = useState(true);

  const albumItems = useMemo(
    () => messages.filter((m) => !m.deleted && (m.type === "image" || m.type === "video" || m.type === "gif")).map((m) => ({ url: fileUrl(m.media), type: m.type === "video" ? "video" : "image", name: m.media_name })),
    [messages]
  );

  const openMedia = (url) => {
    const idx = albumItems.findIndex((a) => a.url === url);
    setViewerIndex(idx >= 0 ? idx : 0);
    setViewerOpen(true);
  };

  useEffect(() => {
    if (chat) setReadAloud(localStorage.getItem(`rc_aloud_${chat.id}`) === "1");
  }, [chat]);

  // Reset pagination state when switching chats.
  useEffect(() => { setHasMore(true); setLoadingOlder(false); lastIdRef.current = null; }, [chat?.id]);

  // Quick call-back from the Calls tab: auto-start the call once the chat is open.
  useEffect(() => {
    if (autoCall && canCall) { setCall({ video: !!autoCall.video }); onAutoCallConsumed && onAutoCallConsumed(); }
    else if (autoCall) { onAutoCallConsumed && onAutoCallConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCall]);

  // Auto-scroll to bottom only when a new message lands at the end (not when older ones are prepended).
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id;
    if (lastId && lastId !== lastIdRef.current) {
      const initial = lastIdRef.current === null;
      lastIdRef.current = lastId;
      endRef.current?.scrollIntoView({ behavior: initial ? "auto" : "smooth" });
    }
  }, [messages]);

  // Preserve scroll position after older messages are prepended.
  useLayoutEffect(() => {
    if (adjustRef.current != null && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight - adjustRef.current;
      adjustRef.current = null;
    }
  }, [messages]);

  const loadOlder = async () => {
    if (loadingOlder || !hasMore || !messages.length) return;
    const before = messages[0]?.created_at;
    if (!before) return;
    setLoadingOlder(true);
    try {
      const { data } = await api.get(`/chats/${chat.id}/messages`, { params: { before, limit: 20 } });
      if (data.length < 20) setHasMore(false);
      if (data.length) {
        adjustRef.current = scrollRef.current?.scrollHeight || 0;
        setMessages((prev) => {
          const existing = new Set(prev.map((m) => m.id));
          const older = data.filter((m) => !existing.has(m.id));
          return older.length ? [...older, ...prev] : prev;
        });
      }
    } catch (e) { /* offline: keep what we have */ }
    finally { setLoadingOlder(false); }
  };

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop < 80 && hasMore && !loadingOlder) loadOlder();
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 140);
  };

  // Read aloud incoming
  useEffect(() => {
    if (!readAloud || !messages.length) return;
    const last = messages[messages.length - 1];
    if (last.sender_id !== user.id && last.type === "text" && last.text && last.id !== lastSpokenRef.current) {
      lastSpokenRef.current = last.id;
      try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(last.text)); } catch (e) {}
    }
  }, [messages, readAloud, user.id]);

  const peerId = chat?.peer?.id;
  const canCall = chat?.type === "direct";
  const isGroup = chat?.type === "group";
  const isReadonly = chat?.readonly || chat?.type === "broadcast" || (chat?.type === "channel" && chat?.can_post === false);
  const setDisappearing = async (seconds) => {
    setDispMenu(false);
    try { await api.post(`/chats/${chat.id}/disappearing`, { seconds }); toast.success(seconds ? "Disappearing messages on" : "Disappearing messages off"); }
    catch { toast.error("Only admins can change this"); }
  };
  const createPoll = async (data) => {
    setPollOpen(false);
    try { await api.post("/polls", { chat_id: chat.id, ...data }); }
    catch { toast.error("Could not create poll"); }
  };
  const receiptsOn = (chat?.peer?.read_receipts !== false) && (user?.read_receipts !== false);

  // Group consecutive images from the same sender (sent together) into albums
  const renderItems = useMemo(() => {
    const items = [];
    const nowMs = Date.now();
    const list = messages.filter((m) => !(m.expires_at && new Date(m.expires_at).getTime() <= nowMs));
    let i = 0;
    while (i < list.length) {
      const m = list[i];
      if (m.type === "image" && !m.deleted) {
        const run = [m];
        let j = i + 1;
        while (
          j < list.length && list[j].type === "image" && !list[j].deleted &&
          list[j].sender_id === m.sender_id &&
          Math.abs(new Date(list[j].created_at) - new Date(run[run.length - 1].created_at)) < 120000
        ) { run.push(list[j]); j++; }
        if (run.length >= 2) { items.push({ kind: "album", key: m.id, msgs: run, sender_id: m.sender_id, created_at: m.created_at }); i = j; continue; }
      }
      items.push({ kind: "msg", key: m.id, m, created_at: m.created_at });
      i++;
    }
    return items;
  }, [messages]);

  const fmtDur = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const dayLabel = (date) => {
    const d = dayjs(date);    if (d.isSame(dayjs(), "day")) return t("today");
    if (d.isSame(dayjs().subtract(1, "day"), "day")) return t("yesterday");
    return d.format(d.isSame(dayjs(), "year") ? "D MMMM" : "D MMMM YYYY");
  };

  const doSend = async (payload) => {
    await sendMessage({ chat_id: chat.id, ...payload });
    playSent();
    haptic("impactLight");
  };

  // ---- Reply-to-message ----
  const [reply, setReply] = useState(null); // { id, sender_name, preview, type }
  const startReply = (m) => {
    const preview = (m.type === "text" || m.type === "sticker") ? (m.text || "") : `[${msgType(m.type)}]`;
    const sender_name = m.sender_id === user.id ? t("you") : (m.sender_name || chat.title || "");
    setReply({ id: m.id, sender_name, preview: preview.slice(0, 120), type: msgType(m.type) });
  };
  const withReply = (payload) => {
    if (!reply) return payload;
    const p = { ...payload, reply_to: reply.id, reply_snippet: { sender_name: reply.sender_name, preview: reply.preview, type: reply.type } };
    setReply(null);
    return p;
  };

  // ---- Undo-before-sending (setting: rc_undo_send default on, rc_undo_seconds default 3) ----
  const [pendingCount, setPendingCount] = useState(null);
  const pendingRef = useRef(null); // { payload, onCancel }
  const pendingTimer = useRef(null);
  const pendingInterval = useRef(null);

  const clearSendTimers = () => {
    clearTimeout(pendingTimer.current);
    clearInterval(pendingInterval.current);
    pendingTimer.current = null;
    pendingInterval.current = null;
  };

  const queueSend = (payload, onCancel) => {
    const enabled = localStorage.getItem("rc_undo_send") !== "0"; // default on
    if (!enabled) return doSend(payload);
    if (pendingRef.current) { doSend(pendingRef.current.payload); clearSendTimers(); } // flush previous
    const secs = parseInt(localStorage.getItem("rc_undo_seconds") || "3", 10);
    pendingRef.current = { payload, onCancel };
    setPendingCount(secs);
    pendingInterval.current = setInterval(() => setPendingCount((c) => (c > 1 ? c - 1 : c)), 1000);
    pendingTimer.current = setTimeout(() => {
      doSend(payload);
      pendingRef.current = null;
      clearSendTimers();
      setPendingCount(null);
    }, secs * 1000);
  };

  const cancelPending = () => {
    clearSendTimers();
    const p = pendingRef.current;
    pendingRef.current = null;
    setPendingCount(null);
    p?.onCancel?.();
  };

  useEffect(() => () => { // flush any pending send on unmount so nothing is lost
    if (pendingRef.current) { doSend(pendingRef.current.payload); clearSendTimers(); }
  }, []); // eslint-disable-line

  const sendGif = (url, kind) => {
    const type = kind === "sticker" || kind === "sticker-gif" ? "sticker" : "gif";
    queueSend(withReply({ type, media: url, media_name: kind }));
    setGifPicker(null);
    setAttachOpen(false);
  };

  const sendText = async () => {
    const val = text.trim();
    if (!val) return;
    setText("");
    queueSend(withReply({ type: "text", text: val }), () => setText(val));
  };

  const onType = (e) => {
    setText(e.target.value);
    sendWs({ type: "typing", chat_id: chat.id, is_typing: true });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => sendWs({ type: "typing", chat_id: chat.id, is_typing: false }), 2000);
  };

  const attach = async (e, type) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast.error("Max 500MB"); e.target.value = ""; return; }
    setUploadPct(0);
    try {
      if (type === "image") {
        if (dataSaverOn()) file = await compressImage(file);
        const meta = await encodeImageMeta(file);
        const { url } = await uploadFileChunked(file, setUploadPct);
        recordSent(file.size, chat.id);
        queueSend(withReply({ type, media: url, media_name: file.name, ...meta }));
      } else if (type === "video") {
        let extra = {};
        const poster = await videoPoster(file);
        if (poster) {
          const meta = await encodeImageMeta(poster);
          const { url: posterUrl } = await uploadFileChunked(poster);
          recordSent(poster.size, chat.id);
          extra = { poster: posterUrl, ...meta };
        }
        const { url } = await uploadFileChunked(file, setUploadPct);
        recordSent(file.size, chat.id);
        queueSend(withReply({ type, media: url, media_name: file.name, ...extra }));
      } else {
        const { url } = await uploadFileChunked(file, setUploadPct);
        recordSent(file.size, chat.id);
        queueSend(withReply({ type, media: url, media_name: file.name }));
      }
    } catch (err) {
      toast.error("Upload failed — check your connection");
    } finally {
      setUploadPct(null);
      e.target.value = "";
    }
  };

  const attachImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter((f) => f.size <= 500 * 1024 * 1024);
    if (valid.length < files.length) toast.error("Some images exceeded 500MB and were skipped");
    for (const raw of valid) {
      const file = dataSaverOn() ? await compressImage(raw) : raw;
      setUploadPct(0);
      try {
        const meta = await encodeImageMeta(file);
        const { url } = await uploadFileChunked(file, setUploadPct);
        recordSent(file.size, chat.id);
        queueSend(withReply({ type: "image", media: url, media_name: file.name, ...meta }));
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploadPct(null);
    e.target.value = "";
  };

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 });
      const chunks = [];
      rec.ondataavailable = (ev) => chunks.push(ev.data);
      rec.onstop = async () => {
        const cancelled = recorderRef.current?._cancelled;
        stream.getTracks().forEach((tr) => tr.stop());
        if (cancelled) return;
        const blob = new Blob(chunks, { type: "audio/webm" });
        const f = new File([blob], "voice.webm", { type: "audio/webm" });
        setUploadPct(0);
        try {
          const { url } = await uploadFileChunked(f, setUploadPct);
          recordSent(f.size, chat.id);
          queueSend(withReply({ type: "audio", media: url, media_name: "voice.webm" }));
        } catch (e) { toast.error("Upload failed"); } finally { setUploadPct(null); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) { toast.error("Microphone access denied"); }
  };

  const cancelRecord = () => {
    if (recorderRef.current) { recorderRef.current._cancelled = true; recorderRef.current.stop(); }
    setRecording(false);
  };

  // recording timer
  useEffect(() => {
    if (!recording) { setRecSeconds(0); return; }
    const id = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [recording]);

  const react = async (id, emoji) => { await api.post(`/messages/${id}/react`, { emoji }); };
  const del = async (id) => { await api.delete(`/messages/${id}`); };

  const [forwardMsgs, setForwardMsgs] = useState(null);
  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success(t("copied")); }
    catch (e) {
      try { const ta = document.createElement("textarea"); ta.value = text || ""; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); toast.success(t("copied")); }
      catch (e2) { toast.error("Copy failed"); }
    }
  };

  const isBlocked = !!peerId && (user?.blocked || []).includes(peerId);
  const toggleBlock = async () => {
    setHdrMenu(false);
    if (!peerId) return;
    try {
      if (isBlocked) {
        await api.post(`/users/${peerId}/unblock`);
        setUser({ ...user, blocked: (user.blocked || []).filter((b) => b !== peerId) });
        toast(t("unblocked_toast"));
      } else {
        await api.post(`/users/${peerId}/block`);
        setUser({ ...user, blocked: [...(user.blocked || []), peerId] });
        toast(t("blocked_toast"));
      }
    } catch (e) { toast.error("Failed"); }
  };

  const toggleAloud = () => {
    const v = !readAloud;
    setReadAloud(v);
    localStorage.setItem(`rc_aloud_${chat.id}`, v ? "1" : "0");
    if (!v) window.speechSynthesis.cancel();
    toast(v ? "Read aloud on" : "Read aloud off");
  };

  const statusLine = useMemo(() => {
    if (typingName) return <span className="text-[var(--rc-gold)]">{t("typing")}</span>;
    if (chat?.type === "direct") return chat.peer?.online ? <span className="text-emerald-400">{t("online")}</span> : "offline";
    if (chat?.type === "group") return `${chat.members?.length || 0} members`;
    return "";
  }, [typingName, chat, t]);

  if (!chat) {
    return (
      <div className="h-full hidden md:flex flex-col items-center justify-center bg-[var(--rc-bg)] rc-grain relative">
        <div className="text-center relative">
          <div className="font-heading text-4xl mb-3">Royal<span className="text-[var(--rc-gold)]">Chat</span> Nova</div>
          <p className="text-[var(--rc-muted)]">Select a conversation to begin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[var(--rc-bg)] min-w-0 overflow-hidden relative">
      {/* header */}
      <div className="flex items-center gap-3 px-3 py-3 border-b border-[var(--rc-border)] bg-[var(--rc-bg)]/80 backdrop-blur-xl">
        <button onClick={onBack} className="md:hidden text-[var(--rc-silver)]" data-testid="thread-back-btn"><ArrowLeft size={22} /></button>
        <div className="h-10 w-10 rounded-2xl grid place-items-center text-[var(--rc-gold)] text-sm font-semibold tracking-wide shrink-0 overflow-hidden border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(chat.title) }}>
          {chat.avatar ? <img src={chat.avatar} alt="" className="h-full w-full object-cover" /> : chat.type === "group" ? <Users size={18} /> : getInitials(chat.title)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-1.5">
            {chat.title}
            {chat.type === "direct" && chat.peer?.email_verified && <BadgeCheck size={16} className="text-[var(--rc-gold)] shrink-0" data-testid="verified-badge-header" />}
            {chat.peer?.flag}
          </div>
          <div className="text-xs text-[var(--rc-muted)]">{statusLine}</div>
        </div>
        {albumItems.length > 0 && (
          <button onClick={() => setAlbumOpen(true)} data-testid="album-btn" title="Media album" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Images size={18} /></button>
        )}
        {chat.type === "channel" && chat.is_admin && (
          <button onClick={shareChannel} data-testid="share-channel-btn" title="Share channel" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Share2 size={18} /></button>
        )}
        {!isReadonly && (
          <div className="relative">
            <button onClick={() => setDispMenu((v) => !v)} data-testid="disappearing-btn" title="Disappearing messages"
              className={`h-9 w-9 grid place-items-center rounded-full transition-colors ${chat.disappearing ? "text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"}`}><Clock size={18} /></button>
            {dispMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setDispMenu(false)} />
                <div className="absolute right-0 top-11 z-30 w-48 rounded-xl bg-[var(--rc-surface-2)] border border-[var(--rc-border)] py-1 shadow-xl rc-pop" data-testid="disappearing-menu">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-[var(--rc-muted)]">Disappearing messages</div>
                  {[{ s: 0, l: "Off" }, { s: 3600, l: "1 hour" }, { s: 86400, l: "24 hours" }, { s: 604800, l: "7 days" }].map((o) => (
                    <button key={o.s} onClick={() => setDisappearing(o.s)} data-testid={`disappearing-${o.s}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--rc-surface)] ${(chat.disappearing || 0) === o.s ? "text-[var(--rc-gold)]" : ""}`}>{o.l}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <button onClick={toggleAloud} data-testid="read-aloud-toggle" title={t("read_aloud")} className={`h-9 w-9 grid place-items-center rounded-full transition-colors ${readAloud ? "text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"}`}>
          {readAloud ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
        {canCall && <button onClick={() => setCall({ video: false })} data-testid="audio-call-btn" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Phone size={18} /></button>}
        {canCall && <button onClick={() => setCall({ video: true })} data-testid="video-call-btn" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Video size={18} /></button>}
        {isGroup && <button onClick={() => onStartGroupCall(false)} data-testid="group-audio-call-btn" title="Group audio call" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Phone size={18} /></button>}
        {isGroup && <button onClick={() => onStartGroupCall(true)} data-testid="group-video-call-btn" title="Group video call" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><Video size={18} /></button>}
        {chat.type === "direct" && (
          <div className="relative">
            <button onClick={() => setHdrMenu((m) => !m)} data-testid="thread-menu-btn" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:bg-[var(--rc-surface)]"><MoreVertical size={18} /></button>
            {hdrMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setHdrMenu(false)} />
                <div className="absolute right-0 top-11 z-30 w-44 rounded-xl bg-[var(--rc-surface-2)] border border-[var(--rc-border)] py-1 shadow-xl rc-pop">
                  <button onClick={toggleBlock} data-testid="thread-block-btn"
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-[var(--rc-surface)] ${isBlocked ? "text-[var(--rc-gold)]" : "text-red-400"}`}>
                    <Ban size={15} /> {isBlocked ? t("unblock") : t("block_contact")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* pinned message announcement banner */}
      {chat.pinned_message && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--rc-border)] bg-[var(--rc-gold)]/5" data-testid="pinned-banner">
          <Pin size={15} className="text-[var(--rc-gold)] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wide text-[var(--rc-gold)]">{t("pinned") || "Pinned message"}</div>
            <div className="text-xs text-[var(--rc-text)] truncate">
              <span className="text-[var(--rc-muted)]">{chat.pinned_message.sender_name}: </span>{chat.pinned_message.text}
            </div>
          </div>
          {canPin && (
            <button onClick={unpin} data-testid="unpin-btn" title="Unpin" className="text-[var(--rc-muted)] hover:text-[var(--rc-text)] shrink-0"><XIcon size={16} /></button>
          )}
        </div>
      )}

      {/* messages */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-6 space-y-3 no-scrollbar">
        {hasMore && messages.length >= 20 && (
          <div className="flex justify-center pb-2" data-testid="load-older-row">
            <button onClick={loadOlder} disabled={loadingOlder} data-testid="load-older-btn"
              className="flex items-center gap-2 text-xs text-[var(--rc-muted)] bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-full px-3 py-1.5 hover:text-[var(--rc-gold)]">
              {loadingOlder ? <Loader2 size={13} className="animate-spin" /> : null}
              {loadingOlder ? t("loading_older") : t("load_older")}
            </button>
          </div>
        )}
        {renderItems.map((it, i) => {
          const prev = renderItems[i - 1];
          const showSep = !prev || !dayjs(it.created_at).isSame(prev.created_at, "day");
          return (
            <React.Fragment key={it.key}>
              {showSep && (
                <div className="flex justify-center my-3" data-testid="date-separator">
                  <span className="text-[11px] font-medium text-[var(--rc-muted)] bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-full px-3 py-1">
                    {dayLabel(it.created_at)}
                  </span>
                </div>
              )}
              {it.kind === "album" ? (
                <AlbumBubble msgs={it.msgs} mine={it.sender_id === user.id} peerId={peerId} receiptsOn={receiptsOn} onOpenMedia={openMedia} onForward={(msgs) => setForwardMsgs(msgs)} />
              ) : (
                it.m.type === "call" && !it.m.deleted
                  ? <CallBubble m={it.m} mine={it.m.sender_id === user.id} onCall={(video) => canCall && setCall({ video })} />
                  : <Bubble m={it.m} mine={it.m.sender_id === user.id} peerId={peerId} receiptsOn={receiptsOn} onReact={react} onDelete={del} onOpenMedia={openMedia} onCopy={copyText} onForward={(m) => setForwardMsgs([m])} onReply={startReply} onPin={pinMessage} canPin={canPin} onCall={(video) => canCall && setCall({ video })} />
              )}
            </React.Fragment>
          );
        })}
        {typingName && (
          <div className="flex gap-1 px-3">
            <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" />
            <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.2s" }} />
            <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.4s" }} />
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Jump to latest */}
      {!atBottom && (
        <button onClick={() => endRef.current?.scrollIntoView({ behavior: "smooth" })} data-testid="jump-to-latest-btn"
          title={t("jump_to_latest")}
          className="absolute right-4 bottom-24 z-30 h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-surface-2)] border border-[var(--rc-border)] text-[var(--rc-gold)] shadow-lg rc-pop hover:bg-[var(--rc-surface)] transition-colors">
          <ChevronDown size={22} />
        </button>
      )}

      {/* emoji / sticker picker */}
      {picker && (
        <div className="px-4 py-3 border-t border-[var(--rc-border)] bg-[var(--rc-surface)] grid grid-cols-8 gap-2 rc-pop max-h-52 overflow-y-auto no-scrollbar" data-testid="picker-panel">
          {(picker === "emoji" ? EMOJIS : STICKERS).map((e) => (
            <button key={e} onClick={() => picker === "emoji" ? setText((t2) => t2 + e) : (doSend(withReply({ type: "sticker", text: e })), setPicker(null))}
              className={`${picker === "sticker" ? "text-3xl" : "text-2xl"} hover:scale-125 transition-transform`}>{e}</button>
          ))}
        </div>
      )}

      {/* GIF / animated sticker picker */}
      {gifPicker && (
        <GifPicker onSelect={sendGif} onClose={() => setGifPicker(null)} />
      )}

      {/* upload progress */}
      {uploadPct !== null && (
        <div className="px-4 py-2 border-t border-[var(--rc-border)] bg-[var(--rc-surface)] flex items-center gap-3 text-sm" data-testid="upload-progress">
          <Loader2 size={16} className="animate-spin text-[var(--rc-gold)]" />
          <div className="flex-1 h-1.5 rounded-full bg-[var(--rc-bg)] overflow-hidden">
            <div className="h-full bg-[var(--rc-gold)] transition-all" style={{ width: `${uploadPct}%` }} />
          </div>
          <span className="text-[var(--rc-muted)]">{uploadPct}%</span>
        </div>
      )}

      {/* input */}
      {isReadonly ? (
        <div className="px-4 py-4 border-t border-[var(--rc-border)] bg-[var(--rc-bg)]/80 backdrop-blur-xl text-center text-sm text-[var(--rc-muted)]" data-testid="readonly-notice">
          🔒 {chat?.type === "channel" ? t("channel_readonly_notice") : t("readonly_notice")}
        </div>
      ) : (
      <div ref={composerRef} className="relative px-2 sm:px-3 py-3 border-t border-[var(--rc-border)] bg-[var(--rc-bg)]/80 backdrop-blur-xl rc-safe-bottom">
        {/* Reply preview bar */}
        {reply && (
          <div className="w-full mb-2 flex items-center gap-2 rounded-xl bg-[var(--rc-surface)] border-l-2 border-[var(--rc-gold)] px-3 py-2 rc-pop" data-testid="reply-preview-bar">
            <Reply size={16} className="text-[var(--rc-gold)] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[var(--rc-gold)] font-medium truncate">{reply.sender_name}</div>
              <div className="text-xs text-[var(--rc-muted)] truncate">{reply.preview || `[${reply.type}]`}</div>
            </div>
            <button onClick={() => setReply(null)} data-testid="reply-cancel-btn" className="text-[var(--rc-muted)] hover:text-[var(--rc-text)] shrink-0"><X size={16} /></button>
          </div>
        )}

        {/* Undo-before-sending bar */}
        {pendingCount != null && (
          <button onClick={cancelPending} data-testid="undo-send-bar"
            className="w-full mb-2 flex items-center gap-2 rounded-full bg-[var(--rc-bubble-out)] border border-[var(--rc-gold)]/30 text-[var(--rc-gold)] px-4 py-2.5 rc-pop">
            <Undo2 size={16} />
            <span className="flex-1 text-left text-sm font-medium">{t("sending_tap_cancel")}</span>
            <span className="h-6 w-6 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-xs font-bold tabular-nums" data-testid="undo-send-count">{pendingCount}</span>
          </button>
        )}

        {/* Recording bar with timer */}
        {recording && (
          <div className="w-full mb-2 flex items-center gap-3 rounded-full bg-[var(--rc-surface)] border border-red-500/40 px-4 py-2.5 rc-pop" data-testid="recording-bar">
            <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm tabular-nums text-[var(--rc-text)]" data-testid="recording-timer">{fmtDur(recSeconds)}</span>
            <span className="flex-1 text-xs text-[var(--rc-muted)]">{t("recording")}</span>
            <button onClick={cancelRecord} data-testid="recording-cancel" className="text-xs text-red-400 font-medium">{t("cancel")}</button>
          </div>
        )}

        {/* Hidden inputs driven by the attach menu */}
        <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={attachImages} data-testid="image-file-input" />
        <input ref={fileInputRef} type="file" hidden onChange={(e) => attach(e, e.target.files[0]?.type.startsWith("video") ? "video" : "document")} data-testid="doc-file-input" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => attach(e, "image")} data-testid="camera-file-input" />

        {/* Attach menu (WhatsApp-style, appears/disappears; closes on outside click) */}
        {attachOpen && (
          <div className="absolute z-20 bottom-full left-2 mb-2 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] shadow-2xl p-3 rc-pop" data-testid="attach-menu">
              <div className="grid grid-cols-3 gap-3 w-[264px]">
                {[
                  { id: "photos", icon: ImageIcon, label: t("attach_photos"), color: "#7C3AED", action: () => imageInputRef.current?.click() },
                  { id: "camera", icon: Camera, label: t("attach_camera"), color: "#059669", action: () => cameraInputRef.current?.click() },
                  { id: "document", icon: FileText, label: t("attach_document"), color: "#2563EB", action: () => fileInputRef.current?.click() },
                  { id: "gif", icon: Film, label: "GIF", color: "#DB2777", action: () => { setGifPicker("gifs"); setPicker(null); } },
                  { id: "poll", icon: BarChart3, label: t("poll"), color: "#0EA5E9", action: () => setPollOpen(true) },
                  { id: "schedule", icon: Clock, label: t("schedule") || "Schedule", color: "#B45309", action: () => setScheduleOpen(true) },
                ].map((o) => (
                  <button key={o.id} data-testid={`attach-${o.id}`}
                    onClick={() => { setAttachOpen(false); o.action(); }}
                    className="flex flex-col items-center gap-1.5 group">
                    <span className="h-14 w-14 grid place-items-center rounded-full text-white group-hover:scale-105 transition-transform" style={{ backgroundColor: o.color }}>
                      <o.icon size={22} />
                    </span>
                    <span className="text-[11px] text-[var(--rc-muted)]">{o.label}</span>
                  </button>
                ))}
              </div>
            </div>
        )}

        <div className="relative z-20 flex items-end gap-1.5 sm:gap-2">
          <button onClick={() => { setPicker((p) => (p === "emoji" ? null : "emoji")); setGifPicker(null); }} data-testid="emoji-btn"
            className={`h-10 w-9 sm:w-10 shrink-0 grid place-items-center rounded-full transition-colors ${picker === "emoji" ? "text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "text-[var(--rc-silver)] hover:text-[var(--rc-gold)]"}`}>
            <Smile size={22} />
          </button>
          <button onClick={() => { setAttachOpen((o) => !o); setPicker(null); setGifPicker(null); }} data-testid="attach-btn"
            className={`h-10 w-9 sm:w-10 shrink-0 grid place-items-center rounded-full transition-all ${attachOpen ? "text-[var(--rc-gold)] bg-[var(--rc-gold)]/10 rotate-45" : "text-[var(--rc-silver)] hover:text-[var(--rc-gold)]"}`}>
            <Plus size={24} />
          </button>
          <textarea rows={1} value={text} onChange={onType}
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
            placeholder={t("type_message")} data-testid="message-input"
            className="flex-1 min-w-0 resize-none bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-4 py-2.5 max-h-36 focus:outline-none focus:border-[var(--rc-gold)]/50" />
          {text.trim() ? (
            <button onClick={sendText} data-testid="send-btn" className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black hover:bg-[var(--rc-gold-light)] transition-colors"><Send size={18} /></button>
          ) : (
            <button onClick={toggleRecord} data-testid="record-btn" className={`h-10 w-10 sm:h-11 sm:w-11 shrink-0 grid place-items-center rounded-full transition-colors ${recording ? "bg-red-500 text-white rc-ring" : "bg-[var(--rc-gold)] text-black"}`}>
              {recording ? <StopCircle size={18} /> : <Mic size={18} />}
            </button>
          )}
        </div>
      </div>
      )}

      {call && <CallOverlay chat={chat} video={call.video} onClose={() => setCall(null)} sendWs={sendWs} outgoing />}
      {albumOpen && <AlbumViewer items={albumItems} onClose={() => setAlbumOpen(false)} />}
      {viewerOpen && <MediaViewer items={albumItems} startIndex={viewerIndex} onClose={() => setViewerOpen(false)} />}
      {forwardMsgs && <ForwardDialog messages={forwardMsgs} currentChatId={chat.id} onClose={() => setForwardMsgs(null)} />}
      {pollOpen && <PollComposer onClose={() => setPollOpen(false)} onCreate={createPoll} />}
      {scheduleOpen && <ScheduleDialog chatId={chat.id} onClose={() => setScheduleOpen(false)} />}
    </div>
  );
}
